import {
  useAutosaveState,
  useLiveQuery,
  useNote,
  useNoteConflict,
  useStore,
} from "@hq/client-react";
import {
  DictationButton,
  MarkdownNoteEditor,
  SaveStatus,
  useDictation,
  type NoteEditorHandle,
} from "@hq/editor";
import type { PaneComponentProps } from "@hq/layout";
import { NoteHeader } from "@hq/notes-ui";
import { detectPlatform, eventMatches, useShortcut } from "@z/ui";
import { useEffect, useRef, useState } from "react";
import { NoteBreadcrumbs, NoteConnections } from "./NoteConnections";
import { useAppConfig } from "../AppConfig";
import { clearAutoFocus, isPinned } from "../workspace/model";
import { useWorkspace } from "../workspace/WorkspaceContext";

/** Device-local preference: which microphone dictation opens. Absent means the system default. */
const DICTATION_DEVICE_KEY = "dictation.inputDeviceId";

/** A note: header with metadata and save status above the Markdown editor. */
export function NotePane({ pane }: PaneComponentProps) {
  const noteId = String(pane.params.noteId);
  const editorRef = useRef<NoteEditorHandle>(null);
  // Captured once per pane instance: the editor consumes it when the note has loaded, which
  // is after the persisted flag below has already been cleared.
  const [autoFocus] = useState(pane.params.autoFocus === true);
  const clearFlag = pane.params.autoFocus === true;
  const ws = useWorkspace();
  const { data: note } = useNote(noteId);
  const title = note?.title;
  const { data: conflict } = useNoteConflict(noteId);
  const saveState = useAutosaveState(noteId);
  const edited = saveState === "dirty" || saveState === "saving";
  const pinned = isPinned(pane);
  // Dictation needs the server to mint provider tokens; local-only builds show no mic.
  const { rpc } = useAppConfig();
  const store = useStore();
  const { data: savedDevice } = useLiveQuery(
    ["kv"],
    (s) => s.kv.get<unknown>(DICTATION_DEVICE_KEY),
    [],
  );
  const deviceId = typeof savedDevice === "string" && savedDevice ? savedDevice : undefined;
  const dictation = useDictation({
    getToken: () => {
      if (!rpc) throw new Error("Dictation needs a server");
      return rpc.dictation.token();
    },
    insertText: (text) => editorRef.current?.insertText(text),
    setGhostText: (text) => editorRef.current?.setGhostText(text),
    getLine: () => editorRef.current?.getLine() ?? "",
    deviceId,
  });
  const dictationShortcut = useShortcut("editor.dictate");

  // Typing into a preview tab keeps it: the next navigation must not replace an edited note.
  useEffect(() => {
    if (edited && !pinned) ws.pin(pane.id);
  }, [edited, pinned, pane.id, ws]);

  // The tab title follows the note title. The layout tree is an external store from the
  // pane's point of view, so this is a subscription-style sync, not derived render state.
  useEffect(() => {
    if (title !== undefined && title !== pane.title) ws.setTitle(pane.id, title);
  }, [title, pane.id, pane.title, ws]);

  // `autoFocus` is consumed once; a reloaded layout must not steal focus.
  useEffect(() => {
    if (clearFlag) ws.setTree((t) => clearAutoFocus(t, pane.id));
  }, [clearFlag, pane.id, ws]);

  return (
    <div
      className="@container/note flex h-full min-h-0 flex-col overflow-y-auto"
      onKeyDownCapture={(event) => {
        if (
          rpc &&
          dictationShortcut &&
          eventMatches(event, dictationShortcut, detectPlatform()) &&
          !event.nativeEvent.isComposing &&
          !event.repeat
        ) {
          event.preventDefault();
          event.stopPropagation();
          dictation.toggle();
        }
      }}
    >
      <NoteHeader
        noteId={noteId}
        className="mx-auto w-full max-w-[800px] shrink-0"
        onSelectFolder={(folderId) =>
          ws.open(folderId ? { paneType: "folder", folderId } : { paneType: "allNotes" }, {
            mode: "tab",
          })
        }
        onDeleted={() => ws.close(pane.id)}
        onEnterContent={() => editorRef.current?.focus()}
        renderLayout={({ header, breadcrumb, actions }) => (
          <MarkdownNoteEditor
            ref={editorRef}
            toolbarStart={breadcrumb}
            toolbarEnd={
              <>
                {rpc && (
                  <DictationButton
                    {...dictation}
                    deviceId={deviceId}
                    onSelectDevice={(next) => {
                      // A new input applies to the next start; end the current run so it is clear.
                      dictation.stop();
                      void (next
                        ? store.kv.set(DICTATION_DEVICE_KEY, next)
                        : store.kv.delete(DICTATION_DEVICE_KEY));
                    }}
                  />
                )}
                {actions}
              </>
            }
            beforeContent={
              <>
                {conflict && (
                  <div
                    className="mx-auto w-full max-w-[800px] p-3 text-sm text-danger"
                    role="status"
                  >
                    A conflicting version was preserved.{" "}
                    <button
                      type="button"
                      className="underline"
                      onClick={() => ws.open({ paneType: "settings" })}
                    >
                      Review in Settings
                    </button>
                  </div>
                )}
                {note && <NoteBreadcrumbs note={note} />}
                {header}
              </>
            }
            noteId={noteId}
            autoFocus={autoFocus}
            onOpenNote={(id) => ws.open({ paneType: "note", noteId: id }, { mode: "tab" })}
            className="h-auto min-h-48 shrink-0 overflow-visible"
          />
        )}
      >
        <SaveStatus noteId={noteId} />
      </NoteHeader>
      {note && <NoteConnections note={note} />}
    </div>
  );
}
